import { Box, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import KlasisList, { IKlasisListRow } from "../components/KlasisList";
import NotFoundBlock from "../components/NotFoundBlock";
import ClassifierRecordActions from "../components/classifier/ClassifierRecordActions";
import ClassifierRecordDetails from "../components/classifier/ClassifierRecordDetails";
import { ApiRoutes } from "../configuration/apiRoutes";
import { defaultLocale as defaultLanguageCode } from "../constants/languages";
import SEARCH_PARAMS from "../constants/searchParams";
import {
    resetBreadcrumbState,
    setBreadcrumbClassifierCode,
    setBreadcrumbClassifierRecordCode,
} from "../features/breadcrumbs";
import { useAppDispatch } from "../hooks/redux";
import { Translations } from "../localization/translations";
import { ClassifierRecordDetailsDto } from "../models/ClassifierRecordDetailsDto";
import { RecordHistoryResponse } from "../models/RecordHistoryResponse";
import PageContent from "../parts/page-content/PageContent";
import { RestService } from "../utils/axios";

const ItemColumnStyle = {
    display: "flex",
    flexDirection: "column",
};

const ClassifierRecord: React.FC = () => {
    let { id, code, locale } = useParams();

    const appDispatch = useAppDispatch();
    const { t } = useTranslation();
    const [search, setSearch] = useSearchParams();

    const classifierLocale = search.get(SEARCH_PARAMS.classifierLocale);

    const [collapsedItemIds, setCollapsedItemIds] = useState<Array<number>>([]);
    const [data, setData] = useState<RecordHistoryResponse>();
    const [isNotFound, setIsNotFound] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const recordName = data?.items.length ? data?.items[0].name : "";

    const refreshData = useCallback(() => {
        if (!id) {
            setIsNotFound(true);
            return;
        }

        setIsLoading(true);

        RestService.Get<RecordHistoryResponse>(
            ApiRoutes.Classifiers.GetRecordHistory({
                classifierId: id,
                code: code ?? "",
                localeCode: locale ?? defaultLanguageCode,
                recordLocaleCode: classifierLocale ?? defaultLanguageCode,
            }),
            (response: RecordHistoryResponse) => {
                if (!response.items.length) {
                    setIsNotFound(true);
                    return;
                }

                appDispatch(
                    setBreadcrumbClassifierCode(response.classifierAbbreviation)
                );

                appDispatch(
                    setBreadcrumbClassifierRecordCode(
                        response.items.length ? response.items[0].code : ""
                    )
                );

                setData(response);
            },
            (error: AxiosError) => {
                if (error.response?.status === 404) setIsNotFound(true);
                console.log("Error: " + error.message);
            },
            () => {
                setIsLoading(false);
            }
        );
    }, [id, code, locale, classifierLocale, appDispatch]);

    const getValidityPeriodText = (
        item: ClassifierRecordDetailsDto
    ): string => {
        let text = `${t(Translations.common.label.validFrom)}: ${new Date(
            item.validFrom
        ).toLocaleDateString(locale)}`;

        if (item.validTo)
            text += `, ${t(Translations.common.label.validTo)}: ${new Date(
                item.validTo
            ).toLocaleDateString(locale)}`;

        return text;
    };

    const isItemDataExpanded = (
        targetItem: ClassifierRecordDetailsDto
    ): boolean =>
        collapsedItemIds.findIndex((item) => targetItem.id === item) === -1;

    const changeItemExpandState = (
        targetItem: ClassifierRecordDetailsDto,
        shouldBeExpanded: boolean
    ): void => {
        const collapsedItemIndex = collapsedItemIds.findIndex(
            (item) => targetItem.id === item
        );
        const isCurrentlyExpanded = collapsedItemIndex === -1;

        if (isCurrentlyExpanded === shouldBeExpanded) return;

        let newCollapsedItemIds = [...collapsedItemIds];

        if (shouldBeExpanded) newCollapsedItemIds.splice(collapsedItemIndex, 1);
        else newCollapsedItemIds.push(targetItem.id);

        setCollapsedItemIds(newCollapsedItemIds);
    };

    const renderItem = (item: ClassifierRecordDetailsDto): JSX.Element => {
        return (
            <>
                <Box
                    sx={{
                        paddingLeft: "1rem",
                        paddingRight: "1rem",
                    }}
                >
                    <Typography>{getValidityPeriodText(item)}</Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                    }}
                >
                    <Box sx={ItemColumnStyle}>
                        <Typography
                            paddingLeft={2}
                            color="text.secondary"
                            lineHeight="2.5rem"
                        >
                            <b>{item.code}</b>
                        </Typography>
                    </Box>
                    <Box sx={{ ...ItemColumnStyle, flex: 1 }}>
                        <Typography
                            color="primary"
                            display="inline-block"
                            lineHeight="2.5rem"
                            marginLeft="1rem"
                        >
                            {item.name}
                        </Typography>
                    </Box>
                    <Box sx={ItemColumnStyle}>
                        <Box>
                            <ClassifierRecordActions
                                classifierId={id ?? ""}
                                classifierRecordCode={item.code}
                                isDataExpanded={
                                    collapsedItemIds.indexOf(item.id) === -1
                                }
                                hideOpenLink={true}
                                hideCopyLink={true}
                                onExpandedStateChange={(
                                    newExpandedState: boolean
                                ) =>
                                    changeItemExpandState(
                                        item,
                                        newExpandedState
                                    )
                                }
                            />
                        </Box>
                    </Box>
                </Box>
                {isItemDataExpanded(item) && (
                    <Box sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}>
                        <ClassifierRecordDetails prefetchedData={item} />
                    </Box>
                )}
            </>
        );
    };

    const getRows = (): IKlasisListRow[] => {
        return (
            data?.items.map((item) => {
                const row: IKlasisListRow = {
                    cols: [
                        {
                            children: renderItem(item),
                        },
                    ],
                };
                return row;
            }) ?? []
        );
    };

    useEffect(() => {
        refreshData();

        return () => {
            appDispatch(resetBreadcrumbState());
        };
    }, [refreshData]);

    if (isNotFound) return <NotFoundBlock />;

    return (
        <PageContent
            title={recordName}
            showBreadcrumbs={true}
            showTitleBarCommands={true}
            isLoading={isLoading}
        >
            <KlasisList rows={getRows()} />
        </PageContent>
    );
};

export default ClassifierRecord;
